import React from 'react'
import moment from 'moment'

import { formatPhoneNumber } from 'utils/formatPhoneNumber'
import type {
  BookingMasterData,
  MasterParameter,
} from 'repositories/BerthApi/getBookingMasterData/type'

import type { ShipmentDetailPickBooking } from '../type'
import styles from '../style.module.css'

const DASH = '−'

const lookupLabel = (
  params: MasterParameter[] | undefined,
  code: string | null | undefined
): string => {
  if (!code || !params) return DASH
  const found = params.find((p) => String(p.parameterId) === code)
  return found?.parameterName || code
}

const formatDatetime = (value: string | null): string =>
  value ? moment(value).format('YYYY/MM/DD HH:mm') : DASH

const formatPhone = (value: string | null | undefined): string =>
  value ? formatPhoneNumber(value) : DASH

const formatValue = (value: string | null | undefined): string => value || DASH

type RowProps = {
  label: string
  children: React.ReactNode
}

const Row: React.FC<RowProps> = ({ label, children }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    {children}
  </div>
)

type CargoGroupProps = {
  title: string
  goodsOwner: string | null | undefined
  goodsName: string | null | undefined
  packageType: string | null | undefined
  packageTypes: MasterParameter[] | undefined
  goodsAmount: number | null | undefined
}

const CargoGroup: React.FC<CargoGroupProps> = ({
  title,
  goodsOwner,
  goodsName,
  packageType,
  packageTypes,
  goodsAmount,
}) => (
  <div className={styles.col}>
    <div className={styles.subSection}>
      <p className={styles.subSectionTitle}>{title}</p>
      <div className={styles.subSectionContent}>
        <div className={styles.rows}>
          <Row label="荷主">
            <span className={styles.value}>{formatValue(goodsOwner)}</span>
          </Row>
          <Row label="荷物">
            <span className={styles.value}>{formatValue(goodsName)}</span>
          </Row>
          <Row label="荷姿">
            <span className={styles.value}>{lookupLabel(packageTypes, packageType)}</span>
          </Row>
          <Row label="荷量">
            <span className={styles.value}>{goodsAmount != null ? String(goodsAmount) : DASH}</span>
          </Row>
        </div>
      </div>
    </div>
  </div>
)

type Props = {
  pickBooking: ShipmentDetailPickBooking | null
  masterData: BookingMasterData | null
}

export const ShipmentBookingSection: React.FC<Props> = ({ pickBooking, masterData }) => {
  const b = pickBooking

  return (
    <div className={styles.sectionCard}>
      <h2 className={styles.sectionHeader}>出荷元予約</h2>

      {/* 基本情報 */}
      <div className={styles.subSection}>
        <p className={styles.subSectionTitle}>基本情報</p>
        <div className={styles.subSectionContent}>
          <div className={styles.rows}>
            <Row label="予約受付番号">
              <span className={styles.value}>{b?.bookingNo ?? DASH}</span>
            </Row>
            <Row label="予約開始日時">
              <span className={styles.value}>{formatDatetime(b?.startDatetime ?? null)}</span>
            </Row>
            <Row label="予約終了日時">
              <span className={styles.value}>{formatDatetime(b?.endDatetime ?? null)}</span>
            </Row>
            <Row label="バース">
              <span className={styles.value}>{b?.berthName ?? DASH}</span>
            </Row>
          </div>
        </div>
      </div>

      {/* 配車担当情報 */}
      <div className={styles.subSection}>
        <p className={styles.subSectionTitle}>配車担当情報</p>
        <div className={styles.subSectionContent}>
          <div className={styles.rows}>
            <Row label="会社名">
              <span className={styles.value}>{formatValue(b?.clientCompanyName)}</span>
            </Row>
            <Row label="配車担当者名">
              <span className={styles.value}>{formatValue(b?.vehicleControllerName)}</span>
            </Row>
            <Row label="電話番号">
              <span className={styles.value}>{formatPhone(b?.vehicleControllerPhone)}</span>
            </Row>
            <Row label="メールアドレス">
              <span className={styles.value}>{formatValue(b?.vehicleControllerEmail)}</span>
            </Row>
          </div>
        </div>
      </div>

      {/* 運送会社情報 */}
      <div className={styles.subSection}>
        <p className={styles.subSectionTitle}>運送会社情報</p>
        <div className={styles.subSectionContent}>
          <div className={styles.rows}>
            <Row label="運送会社名">
              <span className={styles.value}>{formatValue(b?.driverCompanyName)}</span>
            </Row>
            <Row label="車両番号">
              <span className={styles.value}>{formatValue(b?.vehicleNumber)}</span>
            </Row>
            <Row label="車両形態">
              <span className={styles.value}>
                {lookupLabel(masterData?.vehicleCategories, b?.vehicleType)}
              </span>
            </Row>
            <Row label="ドライバー名">
              <span className={styles.value}>{formatValue(b?.driverName)}</span>
            </Row>
            <Row label="電話番号">
              <span className={styles.value}>{formatPhone(b?.driverPhone)}</span>
            </Row>
            <Row label="メールアドレス">
              <span className={styles.value}>{formatValue(b?.driverEmail)}</span>
            </Row>
          </div>
        </div>
      </div>

      {/* 荷積み・荷降ろし */}
      <div className={styles.twoCols}>
        <CargoGroup
          title="荷積み"
          goodsOwner={b?.import?.goodsOwner}
          goodsName={b?.import?.goodsName}
          packageType={b?.import?.packageType}
          packageTypes={masterData?.packageTypes}
          goodsAmount={b?.import?.goodsAmount}
        />
        <CargoGroup
          title="荷降ろし"
          goodsOwner={b?.export?.goodsOwner}
          goodsName={b?.export?.goodsName}
          packageType={b?.export?.packageType}
          packageTypes={masterData?.packageTypes}
          goodsAmount={b?.export?.goodsAmount}
        />
      </div>
    </div>
  )
}
